'use client';

import { ReviewsDataDocument } from '@packages/gql/generated/shopping/ReviewsDataDocument';
import { Box } from '@packages/shared';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { LoadMore } from '../LoadMore';
import { ratingFilterTagPrefix } from '../config';
import type { CustomerRatingsParams } from '../types';
import { RatingCard } from '../../CustomerRatingsV2/RatingCard/RatingCard';

/* GraphQL */ `
  query ReviewsData(
    $productId: String!
    $locale: String!
    $start: Int!
    $count: Int!
    $filter: String
  ) {
    rating(id: $productId, locale: $locale, start: $start, count: $count, filter: $filter) {
      product {
        reviewCount
        reviews {
          id
          ...RatingCardFragment
        }
      }
    }
  }
`;

/**
 * Paginated list of ratings with load more button
 */
export const RatingList = () => {
  const [cursor, setCursor] = useState(0);
  const {
    customerRatings: { pagingCount },
  } = useConfig();

  const pathname = usePathname();
  const params = useParams<CustomerRatingsParams>();
  const { locale } = useI18n();

  // reset cursor after applying, changing or removing filter, so the reviews are shown from 0 again
  // e.g. 20 reviews were already laoded. Now you click on a filter, therefore a new request is sent to load the reviews for that filter.
  // We dont want to load the reviews from 20 tough, so we have to reset cursor to 0.
  // TODO: We could use a atom for the cursor state and update it on filter click outside of RatingList, so we don't need pathname and the useState
  // I guess the concept is that the review detail page can be accessed with a filter -> thats why it is a param
  useEffect(() => {
    setCursor(0);
  }, [pathname]);

  // file "reviewsPagination.ts" takes care about the stacking of reviews
  const [{ data }] = useQuery({
    query: ReviewsDataDocument,
    variables: {
      productId: (params?.akl ?? params?.slug) as string,
      locale,
      start: cursor,
      count: pagingCount,
      filter: params?.ftag ? params.ftag[0].replace(ratingFilterTagPrefix, '') : undefined,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const product = data?.rating?.product;

  if (!product) {
    return null;
  }

  const { reviews, reviewCount } = product;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          m: 0,
          p: 0,
          mb: 3,
          li: { listStyleType: 'none', '& + li': { mt: [1.5, 2] } },
        }}
        component="ul"
      >
        {reviews.map(({ id, ...review }) => (
          <Box component="li" key={id}>
            <RatingCard data={review} />
          </Box>
        ))}
      </Box>
      <LoadMore
        shown={reviews.length}
        reviewCount={reviewCount}
        onLoadMore={() => setCursor(reviews.length)}
      />
    </>
  );
};
